import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import { router } from './router';
import { vuetify } from './plugins/vuetify';
import { useAuthStore } from './stores/auth';
import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@mdi/font/css/materialdesignicons.css';
import 'vuetify/styles';
import '@/assets/styles/global.css';

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);
app.use(router);
app.use(vuetify);

/**
 * Boot sequence.
 *
 * We block `app.mount()` on the silent /auth/refresh round trip so
 * returning users never see a blank screen while the VPS responds.
 * The `#app-boot` node declared in `index.html` is rendered by the
 * browser immediately (plain HTML + CSS, no framework), and we remove
 * it the moment Vue takes over the DOM. If refresh fails (no cookie,
 * expired, network error), mount still happens — the router guard
 * will redirect to /login.
 *
 * `refreshAccessToken` hydrates BOTH accessToken and user in one call
 * (see the B1/B2 fix), so no follow-up fetchMe is needed on boot.
 */
const auth = useAuthStore(pinia);

function mountApp(): void {
  app.mount('#app');
  const splash = document.getElementById('app-boot');
  splash?.remove();
}

auth
  .refreshAccessToken()
  .catch(() => {
    /* No refresh cookie / expired — guard will send user to /login. */
  })
  .finally(() => {
    auth.notifyBootReady();
    mountApp();
  });
